/** PostCSS plugin: prefix rp-ui rules so they apply only under React island mounts. */
const {
	ISLAND_PORTAL_HOST_SELECTOR,
	ISLAND_ROOT_SELECTOR,
	includesDocumentRootSelector,
	isIslandScopedSelector,
	scopeSelector,
	scopeSelectorEntries,
} = require('./island-root-selector');

/** rp-ui rem tokens assume a 10px root; webstore html is 16px. Rem values are scaled below. */
const RP_UI_ROOT_FONT_SIZE_PX = 10;
const WEBSTORE_ROOT_FONT_SIZE_PX = 16;
const REM_SCALE = RP_UI_ROOT_FONT_SIZE_PX / WEBSTORE_ROOT_FONT_SIZE_PX;

/**
 * @param {number} rem
 * @returns {number}
 */
function scaleRpUiRemToWebstoreRem(rem) {
	return rem * REM_SCALE;
}

/**
 * @param {number} rem
 * @returns {string}
 */
function formatScaledRemValue(rem) {
	return `${parseFloat(rem.toFixed(4))}rem`;
}

/**
 * @param {string} value
 * @returns {string}
 */
function convertRemValuesForWebstoreRoot(value) {
	return value.replace(/(-?\d*\.?\d+)rem\b/g, (match, num) => {
		const rem = parseFloat(num);
		const scaledRem = scaleRpUiRemToWebstoreRem(rem);

		if (Math.abs(scaledRem - rem) < 1e-6) {
			return match;
		}

		return formatScaledRemValue(scaledRem);
	});
}

/**
 * @param {string[]} selectors
 * @returns {boolean}
 */
function areSelectorsScopedToIslandRoot(selectors) {
	return selectors.every((selector) => isIslandScopedSelector(selector));
}

/**
 * rp-ui rem tokens assume a 10px root; webstore html is 16px. Scale rem so rendered px matches
 * rp-ui intent. Skipped when selectors are already scoped so repeat PostCSS passes stay stable.
 *
 * @param {import('postcss').Rule} rule
 * @returns {void}
 */
function rewriteScopedRemValues(rule) {
	rule.walkDecls((decl) => {
		decl.value = convertRemValuesForWebstoreRoot(decl.value);
	});
}

/**
 * rp-ui :root sets font-size: 10px for historical rem math. Rem scaling targets webstore's 16px
 * html root instead; keeping 10px on the island wrapper only shrinks inherited text.
 *
 * @param {import('postcss').Rule} rule
 * @param {string[]} originalSelectors
 * @returns {void}
 */
function stripRpUiHistoricalRootFontSize(rule, originalSelectors) {
	if (!includesDocumentRootSelector(originalSelectors)) {
		return;
	}

	rule.walkDecls((decl) => {
		if (decl.prop === 'font-size' && decl.value.trim() === '10px') {
			decl.remove();
		}
	});
}

/**
 * @param {import('postcss').Root} root
 * @returns {void}
 */
function scopeIslandRootStyles(root) {
	root.walkRules((rule) => {
		const parent = rule.parent;

		if (
			parent !== undefined
			&& parent.type === 'atrule'
			&& parent.name.endsWith('keyframes')
		) {
			return;
		}

		if (rule.selectors === undefined || rule.selectors.length === 0) {
			return;
		}

		const originalSelectors = [...rule.selectors];
		const shouldScaleRemValues = !areSelectorsScopedToIslandRoot(rule.selectors);
		rule.selectors = rule.selectors.flatMap((selector) => scopeSelectorEntries(selector));

		if (shouldScaleRemValues) {
			rewriteScopedRemValues(rule);
		}

		stripRpUiHistoricalRootFontSize(rule, originalSelectors);
	});
}

/**
 * @returns {import('postcss').Plugin}
 */
function postcssScopeIslandRoot() {
	const plugin = (root) => {
		scopeIslandRootStyles(root);
	};

	plugin.postcssPlugin = 'postcss-scope-island-root';

	return plugin;
}

postcssScopeIslandRoot.postcss = true;

module.exports = postcssScopeIslandRoot;
module.exports.convertRemValuesForWebstoreRoot = convertRemValuesForWebstoreRoot;
module.exports.ISLAND_ROOT_SELECTOR = ISLAND_ROOT_SELECTOR;
module.exports.scaleRpUiRemToWebstoreRem = scaleRpUiRemToWebstoreRem;
module.exports.scopeSelector = scopeSelector;
